import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef, useCallback } from 'react';
import { track } from '@/lib/analytics';
import {
  FiArrowLeft,
  FiUpload,
  FiDownload,
  FiTrash2,
  FiX,
  FiFile,
  FiImage,
} from 'react-icons/fi';
import { Card, Button } from '@/components';
import { apiJson } from '@/lib/api';
import { useHouseholds } from '@/lib/household-context';
import type {
  VaultFile,
  HouseholdResponse,
  UserResponse,
  PresignedUrlResponse,
} from '@/lib/types';
import {
  ALL_VAULT_CATEGORY_FILTER,
  VAULT_CATEGORIES,
  getVaultCategoryDefinition,
  type VaultCategory,
  type VaultCategoryFilter,
} from '@/lib/vault-categories';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILES_PER_UPLOAD = 10;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function isImageFile(fileType: string): boolean {
  return fileType.startsWith('image/');
}

function VaultPage() {
  const { beneficiaryId } = Route.useParams();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const [viewerVisible, setViewerVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<VaultFile | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<VaultCategoryFilter>(
    ALL_VAULT_CATEGORY_FILTER
  );
  const [uploadCategoryPickerOpen, setUploadCategoryPickerOpen] = useState(false);
  const [selectedUploadCategory, setSelectedUploadCategory] = useState<VaultCategory>('Other');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const beneficiaryIdNum = parseInt(beneficiaryId, 10);

  useEffect(() => {
    track('Vault Screen Viewed', { beneficiary_id: beneficiaryIdNum });
  }, [beneficiaryIdNum]);

  // Fetch user
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  // Fetch households for household ID
  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: () => apiJson<HouseholdResponse[]>(getToken, '/v1/households'),
  });

  const { currentHouseholdId } = useHouseholds();
  const households = householdsQuery.data ?? [];
  const firstHousehold =
    households.find((h) => h.id === currentHouseholdId) ?? households[0];
  const beneficiary = firstHousehold?.beneficiaries?.find(
    (b) => b.beneficiary_id === beneficiaryIdNum
  );

  // Fetch vault files
  const vaultQuery = useQuery({
    queryKey: ['vault', firstHousehold?.id, beneficiaryIdNum],
    queryFn: () =>
      apiJson<VaultFile[]>(
        getToken,
        `/v1/households/${
          firstHousehold!.id
        }/beneficiaries/${beneficiaryIdNum}/vault`
      ),
    enabled: !!firstHousehold,
  });

  const files = vaultQuery.data ?? [];
  const filteredFiles =
    categoryFilter === ALL_VAULT_CATEGORY_FILTER
      ? files
      : files.filter((f) => getVaultCategoryDefinition(f.category).key === categoryFilter);

  // Delete file mutation
  const deleteMutation = useMutation({
    mutationFn: async (fileId: number) => {
      return apiJson(getToken, `/v1/vault-files/${fileId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vault'] });
      track('Vault File Deleted');
    },
  });

  // Download file
  const handleDownload = useCallback(
    async (file: VaultFile) => {
      try {
        const data = await apiJson<PresignedUrlResponse>(
          getToken,
          `/v1/vault-files/${file.id}/download`
        );
        // Open in new tab for download
        window.open(data.presigned_url, '_blank');
        track('Vault File Downloaded', { file_id: file.id });
      } catch {
        alert('Failed to download file.');
      }
    },
    [getToken]
  );

  // View image
  const handleViewFile = useCallback(
    async (file: VaultFile) => {
      if (!isImageFile(file.file_type)) {
        // Non-image files: just download
        handleDownload(file);
        return;
      }
      try {
        const data = await apiJson<PresignedUrlResponse>(
          getToken,
          `/v1/vault-files/${file.id}/download`
        );
        setDownloadUrl(data.presigned_url);
        setSelectedFile(file);
        setViewerVisible(true);
      } catch {
        alert('Failed to load file.');
      }
    },
    [getToken, handleDownload]
  );

  // Delete confirmation
  const handleDelete = (file: VaultFile) => {
    if (
      window.confirm(`Delete "${file.name}"? This action cannot be undone.`)
    ) {
      deleteMutation.mutate(file.id);
    }
  };

  // Upload files
  const handleUpload = async (selectedFiles: FileList, category: VaultCategory) => {
    if (!firstHousehold) return;
    if (selectedFiles.length > MAX_FILES_PER_UPLOAD) {
      alert(
        `You can upload a maximum of ${MAX_FILES_PER_UPLOAD} files at a time.`
      );
      return;
    }

    setUploading(true);
    let uploadedCount = 0;

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        if (file.size > MAX_FILE_SIZE) {
          alert(`"${file.name}" is too large. Max file size is 50MB.`);
          continue;
        }

        setUploadProgress(`Uploading ${i + 1} of ${selectedFiles.length}...`);

        // Get presigned URL
        const urlData = await apiJson<PresignedUrlResponse>(
          getToken,
          `/v1/households/${firstHousehold.id}/beneficiaries/${beneficiaryIdNum}/vault/upload-url`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: file.name,
              file_type: file.type || 'application/octet-stream',
              file_size: file.size,
              category,
            }),
          }
        );

        // Upload to presigned URL
        await fetch(urlData.presigned_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });

        uploadedCount++;
      }

      if (uploadedCount > 0) {
        await queryClient.invalidateQueries({ queryKey: ['vault'] });
        track('Vault Files Uploaded', { count: uploadedCount });
      }
    } catch {
      alert('Failed to upload one or more files.');
    } finally {
      setUploading(false);
      setUploadProgress('');
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const isLoading = householdsQuery.isLoading || vaultQuery.isLoading;
  const currentUserId = userQuery.data?.id;

  if (isLoading) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Header */}
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between'>
          <Link
            to='/app/settings'
            className='inline-flex items-center gap-2 text-primary hover:underline'
          >
            <FiArrowLeft /> Back to Settings
          </Link>
          <div className='flex gap-2'>
            <input
              ref={fileInputRef}
              type='file'
              multiple
              className='hidden'
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  void handleUpload(e.target.files, selectedUploadCategory);
                }
              }}
            />
            <Button
              size='sm'
              onClick={() => setUploadCategoryPickerOpen(true)}
              disabled={uploading}
            >
              <FiUpload className='w-4 h-4 mr-1.5' />
              {uploading ? uploadProgress || 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6'>
        <h1
          className='text-2xl font-bold text-gray-900 mb-1'
          style={{
            fontFamily:
              "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          {beneficiary?.name ?? 'Unknown'}'s Vault
        </h1>
        <p className='text-gray-500 text-sm mb-4'>
          Securely store and share important documents for{' '}
          {beneficiary?.name ?? 'this beneficiary'}.
        </p>

        <div className='flex flex-wrap gap-2 mb-6'>
          <button
            type='button'
            onClick={() => setCategoryFilter(ALL_VAULT_CATEGORY_FILTER)}
            className={`px-3 py-1 rounded-full text-sm font-semibold border ${
              categoryFilter === ALL_VAULT_CATEGORY_FILTER
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            All
          </button>
          {VAULT_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.key}
                type='button'
                onClick={() => setCategoryFilter(cat.key)}
                className={`px-3 py-1 rounded-full text-sm font-semibold border inline-flex items-center gap-1 ${
                  categoryFilter === cat.key
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-200'
                }`}
              >
                <Icon className='w-3.5 h-3.5' />
                {cat.label}
              </button>
            );
          })}
        </div>

        {filteredFiles.length === 0 ? (
          <Card className='text-center py-12'>
            <FiFile className='w-12 h-12 text-gray-300 mx-auto mb-4' />
            <h3
              className='font-bold text-gray-900 mb-2'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              No files yet
            </h3>
            <p className='text-gray-500 mb-4'>
              Upload documents, photos, or other files to{' '}
              {beneficiary?.name ?? 'this'}'s vault.
            </p>
            <Button
              onClick={() => setUploadCategoryPickerOpen(true)}
              disabled={uploading}
            >
              <FiUpload className='w-4 h-4 mr-1.5' />
              Upload files
            </Button>
          </Card>
        ) : (
          <div className='grid grid-cols-2 sm:grid-cols-3 gap-3'>
            {filteredFiles.map((file) => {
              const categoryDef = getVaultCategoryDefinition(file.category);
              const CatIcon = categoryDef.icon;
              return (
              <div
                key={file.id}
                className='bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer group'
                onClick={() => handleViewFile(file)}
              >
                {/* Thumbnail / Icon area */}
                <div className='aspect-square bg-gray-50 flex items-center justify-center relative'>
                  {isImageFile(file.file_type) ? (
                    <FiImage className='w-12 h-12 text-primary/30' />
                  ) : (
                    <FiFile className='w-12 h-12 text-gray-300' />
                  )}

                  {/* Overlay buttons */}
                  <div className='absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100'>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(file);
                      }}
                      className='p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors'
                      title='Download'
                    >
                      <FiDownload className='w-4 h-4 text-gray-700' />
                    </button>
                    {currentUserId === file.creator_user_id && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(file);
                        }}
                        className='p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors'
                        title='Delete'
                        disabled={deleteMutation.isPending}
                      >
                        <FiTrash2 className='w-4 h-4 text-red-500' />
                      </button>
                    )}
                  </div>
                </div>

                {/* File info */}
                <div className='p-3'>
                  <p className='text-sm font-semibold text-gray-900 truncate'>
                    {file.name}
                  </p>
                  <p className='text-xs text-gray-500 mt-0.5 flex items-center gap-1'>
                    <CatIcon className='w-3 h-3 shrink-0' />
                    <span>{categoryDef.label}</span>
                    <span>&middot;</span>
                    <span>{formatFileSize(file.file_size)}</span>
                  </p>
                  <p className='text-xs text-gray-400'>{formatDate(file.created_at)}</p>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {uploadCategoryPickerOpen && (
        <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6'>
            <h3 className='text-lg font-bold text-gray-900 mb-2'>Choose category</h3>
            <p className='text-sm text-gray-500 mb-4'>Files will be organized under this category.</p>
            <div className='grid grid-cols-2 gap-2'>
              {VAULT_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.key}
                    type='button'
                    onClick={() => {
                      setSelectedUploadCategory(cat.key);
                      setUploadCategoryPickerOpen(false);
                      setTimeout(() => fileInputRef.current?.click(), 0);
                    }}
                    className='flex items-center gap-2 p-3 rounded-xl border border-gray-200 hover:border-primary hover:bg-primary/5 text-left'
                  >
                    <Icon className='w-4 h-4 text-primary shrink-0' />
                    <span className='text-sm font-semibold text-gray-800'>{cat.label}</span>
                  </button>
                );
              })}
            </div>
            <Button
              variant='secondary'
              fullWidth
              className='mt-4'
              onClick={() => setUploadCategoryPickerOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {viewerVisible && selectedFile && downloadUrl && (
        <div
          className='fixed inset-0 bg-black/90 flex items-center justify-center z-50'
          onClick={() => setViewerVisible(false)}
        >
          <button
            onClick={() => setViewerVisible(false)}
            className='absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors z-10'
          >
            <FiX className='w-6 h-6 text-white' />
          </button>

          <div
            className='max-w-4xl max-h-[90vh] p-4'
            onClick={(e) => e.stopPropagation()}
          >
            {isImageFile(selectedFile.file_type) ? (
              <img
                src={downloadUrl}
                alt={selectedFile.name}
                className='max-w-full max-h-[80vh] object-contain rounded-lg'
              />
            ) : (
              <div className='text-center text-white'>
                <FiFile className='w-16 h-16 mx-auto mb-4 opacity-50' />
                <p className='text-lg font-semibold'>{selectedFile.name}</p>
              </div>
            )}

            <div className='flex justify-center gap-3 mt-4'>
              <Button size='sm' onClick={() => handleDownload(selectedFile)}>
                <FiDownload className='w-4 h-4 mr-1.5' />
                Download
              </Button>
              {currentUserId === selectedFile.creator_user_id && (
                <Button
                  variant='danger'
                  size='sm'
                  onClick={() => {
                    handleDelete(selectedFile);
                    setViewerVisible(false);
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <FiTrash2 className='w-4 h-4 mr-1.5' />
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/app/vault/$beneficiaryId')({
  component: VaultPage,
});
